import Layout from 'Layout'
import { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { getNotificationPage, getNotificationPageUnread, getUnreadCount, markAsRead, NotificationData } from './function'
import NotificationItem from './notificationList'
import { Pagination, Select, Popconfirm } from 'antd';
import { API_URL } from 'env'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'
import { FirebaseNotificationToggle } from './notificationSwitch'

const NotificationMainPage = () => {
    const [notifications, setNotifications] = useState<NotificationData[] | []>([])
    const [notificationUnreadLength, setNotificationUnreadLength] = useState(0)
    const [notificationCount, setNotificationCount] = useState(0)
    const [currentPage, setCurrentPage] = useState(1)
    const [filter, setFilter] = useState<'all' | 'read' | 'unread'>('all')
    const { t } = useTranslation();

    async function reloadCurrentPage() {
        setNotifications([]);
        if (filter === 'all') {
            getNotificationPage(currentPage, 10).then(data => {
                if (data && typeof data === 'object' && 'data' in data && Array.isArray(data.data)) {
                    setNotifications(data.data);
                    setNotificationCount(data.count);
                } else if (Array.isArray(data)) {
                    setNotifications(data);
                } else {
                    setNotifications([]);
                }
            });
        } else if (filter === 'read') {
            getNotificationPageUnread(currentPage, 10, true).then(data => {
                if (data && typeof data === 'object' && 'data' in data && Array.isArray(data.data)) {
                    setNotifications(data.data);
                    setNotificationCount(data.count);
                } else if (Array.isArray(data)) {
                    setNotifications(data);
                } else {
                    setNotifications([]);
                }
            });
        } else if (filter === 'unread') {
            getNotificationPageUnread(currentPage, 10).then(data => {
                if (data && typeof data === 'object' && 'data' in data && Array.isArray(data.data)) {
                    setNotifications(data.data);
                    setNotificationCount(data.count);
                } else if (Array.isArray(data)) {
                    setNotifications(data);
                } else {
                    setNotifications([]);
                }
            });
        }

        getUnreadCount().then(count => {
            setNotificationUnreadLength(count);
        });
    }

    async function markAllAsRead() {
        try {
            const user = localStorage.getItem('user');
            if (!user) {
                toast.error('User not logged in');
                return;
            }
            const uid = JSON.parse(user).id;
            if (!uid) {
                toast.error('User ID not found');
                return;
            }
            const res = await fetch(API_URL + `/api/v1/be/notifications/mark-all-as-read`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({ user_id: Number(uid) })
            });
            const data = await res.json();
            if (data.status) {
                toast.success('All notifications marked as read');
                reloadCurrentPage();
            } else {
                toast.error('Failed to mark all as read. Error: ' + (JSON.stringify(data.error) || 'Unknown error'));
            }
        }
        catch (error) {
            toast.error('Network error while marking all as read');
        }
    }

    useEffect(() => {
        if (filter === 'all') {
            getNotificationPage(currentPage, 10).then(data => {
                if (data && typeof data === 'object' && 'data' in data && Array.isArray(data.data)) {
                    setNotifications(data.data);
                    setNotificationCount(data.count);
                } else if (Array.isArray(data)) {
                    setNotifications(data);
                } else {
                    setNotifications([]);
                }
            });
        } else if (filter === 'read') {
            getNotificationPageUnread(currentPage, 10, true).then(data => {
                if (data && typeof data === 'object' && 'data' in data && Array.isArray(data.data)) {
                    setNotifications(data.data);
                    setNotificationCount(data.count);
                } else if (Array.isArray(data)) {
                    setNotifications(data);
                } else {
                    setNotifications([]);
                }
            });
        } else if (filter === 'unread') {
            getNotificationPageUnread(currentPage, 10, false).then(data => {
                if (data && typeof data === 'object' && 'data' in data && Array.isArray(data.data)) {
                    setNotifications(data.data);
                    setNotificationCount(data.count);
                } else if (Array.isArray(data)) {
                    setNotifications(data);
                } else {
                    setNotifications([]);
                }
            });
        }
        getUnreadCount().then(count => {
            setNotificationUnreadLength(count);
        });
    }, [currentPage, filter])

    return (
        <Layout>
            <Helmet>
                <title>
                    {notificationUnreadLength === 0
                        ? 'Notifications'
                        : `(${notificationUnreadLength}) Notifications`}
                </title>
                <meta name="description" content="Notifications page" />
            </Helmet>
            <div style={{ padding: '24px 20px 10px', maxWidth: '900px', margin: '0 auto', width: '100%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                        <Select 
                            defaultValue="all"
                            className='notificationPageSelect'
                            style={{ width: 140 }}
                            onChange={(value: 'all' | 'read' | 'unread') => {
                                setFilter(value);
                                setCurrentPage(1);
                            }}
                            options={[
                                { value: 'all', label: t('All') },
                                { value: 'read', label: t('notification_read') },
                                { value: 'unread', label: t('notification_unread') },
                            ]}
                        />
                        <Popconfirm
                            title="Are you sure to mark all notification as read?"
                            onConfirm={markAllAsRead}
                            okText="Yes"
                            cancelText="No"
                        >
                            <button className='markAllAsReadButton' style={{
                                padding: '6px 14px',
                                background: 'var(--card, #fff)',
                                border: '1px solid var(--border, #e9e6dd)',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontSize: '13px',
                                fontWeight: 600,
                                color: 'var(--ink, #0b1c2e)',
                                transition: 'background .15s'
                            }}>
                                {t('notification_mark_all_read')}
                            </button>
                        </Popconfirm>
                    </div>
                    <div>
                        <span style={{ fontSize: '13px', color: 'var(--ink-soft, #5b6472)', fontWeight: 500 }}>
                            {t('notification_total')}: <b>{notificationCount}</b> | {t('notification_unread')}: <b style={{ color: 'var(--red, #e24b4a)' }}>{notificationUnreadLength}</b>
                        </span>
                    </div>
                </div>
            </div>

            <div style={{ padding: '0 20px 20px', maxWidth: '900px', margin: '0 auto', display: 'flex', flexDirection: 'column', width: '100%', gap: '4px' }}>
                {notifications.length === 0 ? (
                    <div style={{ padding: '60px 20px', textAlign: 'center', color: '#93989f', fontSize: '14px', background: 'var(--card, #fff)', borderRadius: '12px', border: '1px solid var(--border, #e9e6dd)' }}>
                        {t('notification_no_notification')}
                    </div>
                ) : (
                    notifications.map((notification) => (
                        <NotificationItem
                            id={notification.id.toString()}
                            key={notification.id}
                            title={notification.title}
                            message={notification.message}
                            subtitle={notification.subtitle}
                            isRead={notification.is_read}
                            createdAt={notification.createdAt}
                            redirect_url={notification.redirect_url || undefined}
                        />
                    ))
                )}
            </div>

            <div style={{ paddingBottom: '30px' }}>
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <Pagination 
                        current={currentPage} 
                        pageSize={10} 
                        total={notificationCount} 
                        onChange={(page) => setCurrentPage(page)} 
                        showSizeChanger={false} 
                    />
                </div>
            </div>
        </Layout>
    )
}
export default NotificationMainPage